import { motion } from "framer-motion";
import { ExternalLink, ArrowUpRight } from "lucide-react";
import { PORTFOLIO } from "@/constants/testIds";

const projects = [
  {
    id: "bellso",
    name: "bellso.shop",
    url: "https://bellso.shop",
    tag: "E-Commerce",
    desc: "Premium online store with custom motion and Stripe-powered checkout.",
    stats: { metric: "+312%", label: "monthly orders" },
    accent: "from-lime-400/30 to-emerald-500/10",
  },
  {
    id: "xlabelsync",
    name: "xlabelsync.com",
    url: "https://xlabelsync.com",
    tag: "Corporate Identity",
    desc: "Brand-led marketing site with lead-gen funnels and CRM sync.",
    stats: { metric: "2.4s", label: "avg load time" },
    accent: "from-cyan-400/30 to-blue-500/10",
  },
  {
    id: "orbitabot",
    name: "orbitabot.live",
    url: "https://orbitabot.live",
    tag: "SaaS Platform",
    desc: "Bot platform landing with live demos, pricing, and free trial signup.",
    stats: { metric: "98", label: "Lighthouse score" },
    accent: "from-fuchsia-400/30 to-pink-500/10",
  },
  {
    id: "nexso",
    name: "nexso.net",
    url: "https://nexso.net",
    tag: "Digital Agency",
    desc: "Bold agency site with case studies, immersive scroll, and inquiry flow.",
    stats: { metric: "+5x", label: "qualified leads" },
    accent: "from-amber-400/30 to-orange-500/10",
  },
];

export default function Portfolio() {
  return (
    <section id="portfolio" className="section-pad relative bg-[#070707]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-mono-jb text-xs uppercase tracking-[0.3em] text-[#CCFF00]">
              / Selected work
            </span>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold mt-4 leading-[1.05]">
              Sites we've actually built.
            </h2>
            <p className="text-zinc-400 mt-4 max-w-2xl">
              Every live link below is a real project — click to explore.
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {projects.map((p, i) => (
            <motion.a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={PORTFOLIO.card(p.id)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[#0d0d0d] hover:border-[#CCFF00] transition-all"
            >
              {/* Top color band */}
              <div
                className={`absolute inset-x-0 top-0 h-[260px] bg-gradient-to-br ${p.accent} opacity-90`}
              />
              <div className="absolute inset-0 bg-grid opacity-30" />

              <div className="relative p-8 md:p-10">
                {/* Browser mock */}
                <div className="rounded-xl bg-black/80 backdrop-blur border border-white/10 overflow-hidden shadow-2xl">
                  <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-black/60">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#CCFF00]" />
                    <div className="ml-3 flex-1 text-[10px] font-mono-jb text-zinc-500 truncate">
                      {p.url}
                    </div>
                  </div>
                  <div className="h-48 md:h-56 relative overflow-hidden">
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${p.accent}`}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="font-display text-3xl md:text-4xl font-black text-white/90">
                        {p.name}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-start justify-between gap-6">
                  <div>
                    <span className="font-mono-jb text-[10px] uppercase tracking-widest text-[#CCFF00]">
                      {p.tag}
                    </span>
                    <h3 className="font-display text-2xl md:text-3xl font-bold mt-1">
                      {p.name}
                    </h3>
                    <p className="text-zinc-400 mt-2 max-w-md">{p.desc}</p>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="font-display text-3xl md:text-4xl font-black text-[#CCFF00] leading-none">
                      {p.stats.metric}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 font-mono-jb">
                      {p.stats.label}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-2 text-sm text-zinc-300 group-hover:text-[#CCFF00] transition-colors">
                  <ExternalLink className="w-4 h-4" />
                  Visit live site
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
